import React, { useState, useEffect } from "react";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "./firebase";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaLock, FaKey, FaCheckCircle, FaArrowLeft } from "react-icons/fa";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [email, setEmail] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Get the oobCode from URL parameters
    const oobCode = searchParams.get("oobCode");
    const mode = searchParams.get("mode");

    if (!oobCode || mode !== "resetPassword") {
      setError("Invalid or missing reset link. Please request a new password reset.");
      setValidating(false);
      return;
    }

    // Verify the reset code and get the email
    const verifyCode = async () => {
      try {
        const email = await verifyPasswordResetCode(auth, oobCode);
        setEmail(email);
        setValidating(false);
      } catch (err) {
        console.error("Code verification error:", err);
        setError("This reset link is invalid or has expired. Please request a new password reset.");
        setValidating(false);
      }
    };

    verifyCode();
  }, [searchParams]);

  const validatePassword = (pwd) => {
    if (pwd.length < 6) {
      return "Password must be at least 6 characters long.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Validate passwords
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    const oobCode = searchParams.get("oobCode");

    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess("Password reset successfully! Redirecting to login...");
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      console.error("Password reset error:", err);
      
      switch (err.code) {
        case "auth/expired-action-code":
          setError("This reset link has expired. Please request a new password reset.");
          break;
        case "auth/invalid-action-code":
          setError("This reset link is invalid. Please request a new password reset.");
          break;
        case "auth/weak-password":
          setError("Password is too weak. Please choose a stronger password.");
          break;
        default:
          setError(`Failed to reset password: ${err.message}`);
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="font-sans bg-gray-900 text-white min-h-screen">
        <Navbar />
        <section className="relative overflow-hidden py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-sky-900/30 -z-10"></div>
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-center">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-8 shadow-2xl backdrop-blur-sm max-w-md w-full">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                <p className="text-gray-400">Validating reset link...</p>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  if (error && !email) {
    return (
      <div className="font-sans bg-gray-900 text-white min-h-screen">
        <Navbar />
        <section className="relative overflow-hidden py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-sky-900/30 -z-10"></div>
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-center">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-8 shadow-2xl backdrop-blur-sm max-w-md w-full">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaKey className="text-2xl text-red-400" />
                </div>
                <h2 className="text-2xl font-bold mb-4 text-gray-200">Invalid Reset Link</h2>
                <p className="text-gray-400 mb-6">{error}</p>
                <Link
                  to="/forgot-password"
                  className="inline-block px-6 py-3 rounded-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white font-medium transition-all duration-300"
                >
                  Request New Reset Link
                </Link>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="font-sans bg-gray-900 text-white min-h-screen">
      <Navbar />

      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-sky-900/30 -z-10"></div>
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-center gap-12">
          {/* Visual Element */}
          <div className="hidden lg:flex lg:w-1/2 justify-center items-center">
            <div className="relative w-full max-w-lg">
              <div className="absolute -top-10 -left-10 w-32 h-32 bg-blue-600 rounded-full filter blur-3xl opacity-30"></div>
              <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-sky-600 rounded-full filter blur-3xl opacity-30"></div>
              <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-12 shadow-2xl backdrop-blur-sm">
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-sky-600 rounded-full flex items-center justify-center mb-6">
                    <FaLock className="text-4xl text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">Reset Password</h3>
                  <p className="text-gray-400">
                    Enter your new password below
                  </p>
                  {email && (
                    <p className="text-sm text-gray-500 mt-2">
                      Resetting password for: {email}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="w-full lg:w-1/2 max-w-md">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-8 shadow-2xl backdrop-blur-sm">
              <h2 className="text-3xl font-bold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-sky-300">
                Reset Password
              </h2>

              {error && (
                <div className="mb-6 p-4 bg-red-900/50 text-red-300 rounded-lg border border-red-700 text-sm text-center">
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-6 p-4 bg-green-900/50 text-green-300 rounded-lg border border-green-700 text-sm text-center flex items-center justify-center">
                  <FaCheckCircle className="mr-2" />
                  {success}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-400 mb-2">New Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-gray-500" />
                    </div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password (min. 6 characters)"
                      className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-white placeholder-gray-500"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-400 mb-2">Confirm Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-gray-500" />
                    </div>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-white placeholder-gray-500"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || success}
                  className="w-full px-8 py-3 rounded-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white font-medium transition-all duration-300 shadow-lg hover:shadow-blue-500/30 flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Resetting...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </button>
              </form>

              <div className="mt-6 text-center text-sm text-gray-400">
                <Link
                  to="/login"
                  className="inline-flex items-center hover:text-blue-400 hover:underline transition"
                >
                  <FaArrowLeft className="mr-2" />
                  Back to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResetPassword;

